import type {
  ChatMessage,
  TokenUsageMetadata,
} from '../../../components/GlobalChatSystem/types';
import {
  buildResearchDeliverableMessage,
  type RawDeliverableAttachment,
} from './buildResearchDeliverableMessage';
import {
  buildMessageTokenUsage,
  type WireTokenUsage,
} from './buildMessageTokenUsage';
import { isClarifyingSkipBreadcrumbBody } from '../../prest0nSession/clarifyingBreadcrumb';

export interface HandleOrphanResearchDeliverableDoneParams {
  streamingMessageId: string | null;
  attachmentsSerialized?:
    | Array<RawDeliverableAttachment | null | undefined>
    | null;
  surfaceOwnedText?: string;
  addMessage: (message: ChatMessage) => void;
  removeThinkingIndicator: () => void;
  releaseProcessingHeartbeat?: ((threadId?: string | null) => void) | null;
  currentTurnThreadId?: string | null;
  markdownToHtml: (markdown: string) => string;
  onReleaseError?: (error: unknown) => void;
  // Token usage (2026-07-13) — SSE `done` wire usage so the ⓘ popup shows
  // real compute on the full-pipeline DR deliverable. Mapped through the same
  // shared builder as the streaming-finalize branch.
  tokenUsage?: WireTokenUsage;
  tokenUsages?: WireTokenUsage[];
  /** User-message length for the input-token estimate fallback. */
  userMessageLength?: number;
  /**
   * The turn's own durable task id (useGlobalTools' `turnServerTaskId`). It
   * anchors this bubble to the SAME Firestore document the server writes its
   * durable copy of the answer to — without it the two copies both render and
   * the user sees the report twice. See buildResearchDeliverableMessage's
   * return for the full account. Omitted ⇒ unchanged behaviour.
   */
  serverTaskId?: string | null;
  /**
   * INTERSTITIAL-MESSAGE ELIMINATION (owner directive 2026-08-13): true when
   * this turn already finalized the pending widget into a clarifying-
   * questions card. The card owns the surface — a body-only done event must
   * NOT spawn a second message; returning false here lets the caller's
   * clarifying re-finalize block attach the done event's usage to the card
   * itself (the most recent real message).
   */
  clarifyingCardFinalizedThisTurn?: boolean;
}

/**
 * Handles the no-streaming-message DR deliverable completion path.
 *
 * Surface-owning full-pipeline Deep Research turns intentionally do not emit
 * `text` SSE chunks. When the final `done` event carries PDF/DOCX attachments,
 * this creates the final assistant message and clears the pending UI state that
 * would normally be cleared by `finalizeStreamingMessageById`.
 */
export function handleOrphanResearchDeliverableDone({
  streamingMessageId,
  attachmentsSerialized,
  surfaceOwnedText,
  addMessage,
  removeThinkingIndicator,
  releaseProcessingHeartbeat,
  currentTurnThreadId,
  markdownToHtml,
  onReleaseError,
  tokenUsage,
  tokenUsages,
  userMessageLength,
  clarifyingCardFinalizedThisTurn,
  serverTaskId,
}: HandleOrphanResearchDeliverableDoneParams): boolean {
  if (streamingMessageId !== null) {
    return false;
  }

  // INTERSTITIAL-MESSAGE ELIMINATION (owner directive 2026-08-13): a
  // body-only done event must never spawn a standalone bubble when the
  // clarifying card already owns this turn's surface, and the clarifying
  // skip breadcrumb must never become a message body on ANY turn (covers
  // task docs written by pre-fix servers during mixed-version rollouts).
  // Returning false routes the caller to its clarifying re-finalize block,
  // which attaches the done event's usage to the card — the most recent
  // real message — instead of to a phantom bubble.
  const hasRealAttachments = Array.isArray(attachmentsSerialized)
    && attachmentsSerialized.some((att) => att && typeof att === 'object' && att.name);
  if (!hasRealAttachments) {
    if (clarifyingCardFinalizedThisTurn) {
      return false;
    }
    if (isClarifyingSkipBreadcrumbBody(surfaceOwnedText)) {
      return false;
    }
  }

  // Attachment-bearing turns keep their body — unless that body is itself a
  // breadcrumb (never a real report), in which case ship attachments only.
  const reportText =
    typeof surfaceOwnedText === 'string'
    && surfaceOwnedText.length > 0
    && !isClarifyingSkipBreadcrumbBody(surfaceOwnedText)
      ? surfaceOwnedText
      : '';
  const reportHtml = reportText ? markdownToHtml(reportText) : '';
  // The full-pipeline DR report emits no text chunks, so the visible output
  // for the output-token estimate fallback is the report body itself.
  const usage: { tokenUsage?: TokenUsageMetadata; tokenUsages?: TokenUsageMetadata[] } =
    buildMessageTokenUsage(tokenUsage, tokenUsages, {
      outputTextLength: reportText.length,
      userMessageLength,
    });
  const message = buildResearchDeliverableMessage(
    attachmentsSerialized,
    reportText,
    reportHtml,
    usage,
    serverTaskId,
  );

  if (!message) {
    return false;
  }

  addMessage(message);
  removeThinkingIndicator();

  try {
    releaseProcessingHeartbeat?.(currentTurnThreadId ?? null);
  } catch (error) {
    onReleaseError?.(error);
  }

  return true;
}
